import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

const ACCENT = 'var(--rd-primary)';
const DRAG_THRESHOLD = 6;

const dragSession = {
  field: null,
  onReorder: null,
  startX: 0,
  startY: 0,
  active: false
};

function resetDragSession() {
  dragSession.field = null;
  dragSession.onReorder = null;
  dragSession.startX = 0;
  dragSession.startY = 0;
  dragSession.active = false;
}

export function reorderColumnFields(fields, fromField, toField) {
  if (!fromField || !toField || fromField === toField || !Array.isArray(fields)) {
    return null;
  }
  const next = [...fields];
  const from = next.indexOf(fromField);
  const to = next.indexOf(toField);
  if (from < 0 || to < 0) return null;
  next.splice(from, 1);
  next.splice(to, 0, fromField);
  return next;
}

const DraggableColumnHeader = ({ field, onReorder, children }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isOver, setIsOver] = useState(false);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!dragSession.field || dragSession.field !== field) return;
      const dx = Math.abs(event.clientX - dragSession.startX);
      const dy = Math.abs(event.clientY - dragSession.startY);
      if (!dragSession.active && (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD)) {
        dragSession.active = true;
        setIsDragging(true);
      }
    };

    const handlePointerUp = () => {
      if (dragSession.field !== field) return;
      resetDragSession();
      setIsDragging(false);
      setIsOver(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [field]);

  return (
    <Box
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        if (event.target.closest?.('.MuiDataGrid-columnSeparator')) return;
        dragSession.field = field;
        dragSession.onReorder = onReorder;
        dragSession.startX = event.clientX;
        dragSession.startY = event.clientY;
        dragSession.active = false;
      }}
      onPointerEnter={() => {
        if (dragSession.field && dragSession.field !== field) {
          setIsOver(true);
        }
      }}
      onPointerLeave={() => setIsOver(false)}
      onPointerUp={() => {
        if (dragSession.active && dragSession.field && dragSession.field !== field) {
          dragSession.onReorder?.(dragSession.field, field);
        }
        resetDragSession();
        setIsDragging(false);
        setIsOver(false);
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minHeight: 32,
        cursor: 'grab',
        userSelect: 'none',
        opacity: isDragging ? 0.45 : 1,
        borderLeft: isOver ? `3px solid ${ACCENT}` : '3px solid transparent',
        pl: 0.5,
        '&:active': { cursor: 'grabbing' }
      }}
    >
      {children}
    </Box>
  );
};

export default DraggableColumnHeader;
